import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import { SuiContainer } from '@splashthat/sui';
import ColorPickerEditor from 'common/components/ColorPicker';
import BorderEditor from 'common/components/BorderPicker';

import {
    setAllFormButtonBorderSizes,
    setFormButtonBorderRadius,
    setFormButtonBorderSizeTop,
    setFormButtonBorderSizeBottom,
    setFormButtonBorderSizeRight,
    setFormButtonBorderSizeLeft,
    setFormButtonBorderColorDefault,
    setFormButtonBorderColorHover,
    setFormButtonBorderColorFocus,
} from 'common/entities/form/styleConfiguration/actions';
import {
    DEFAULT_BUTTON_BORDER_COLOR,
    DEFAULT_BUTTON_BORDER_RADIUS,
    DEFAULT_BUTTON_BORDER_COLOR_HOVER,
    DEFAULT_BUTTON_BORDER_COLOR_FOCUS,
} from 'common/entities/form/styleConfiguration/defaultValues';
import { getDefaultStyle, styleShapePropTypes } from 'common/entities/form/styleConfiguration/utils';
import { getFormButtonBorderStyles } from 'common/entities/form/styleConfiguration/selectors';
import { getColors } from 'common/entities/color/selectors';
import { getFormId } from '../../../../selectors';

import {
    COLOR_PICKER_DEFAULT,
    COLOR_PICKER_HOVER,
    BUTTON_LABEL_TEXT,
    COLOR_PICKER_FOCUS,
} from '../../../../constants/formStyleLabels';

import { BUTTON_QA } from '../../../../utils/qaIdConstants';

const ButtonBorderPanel = ({
    formId,
    defaultColor,
    hoverColor,
    borderWidths,
    borderRadius,
    colorList,
    focusColor,
    ...props
}) => (
    <SuiContainer className={'BorderColorPicker'} data-qa={`${BUTTON_QA}--label`}>
        <BorderEditor
            borderWidths={borderWidths}
            onBorderWidthsChanged={(width) => props.setAllFormButtonBorderSizes(formId, width)}
            borderRadius={borderRadius}
            onBorderRadiusChanged={(radius) => props.setFormButtonBorderRadius(formId, radius)}
            onChangeTopBorderWidth={(width) => props.setFormButtonBorderSizeTop(formId, width)}
            onChangeBottomBorderWidth={(width) => props.setFormButtonBorderSizeBottom(formId, width)}
            onChangeRightBorderWidth={(width) => props.setFormButtonBorderSizeRight(formId, width)}
            onChangeLeftBorderWidth={(width) => props.setFormButtonBorderSizeLeft(formId, width)}
            borderOptionsQa={`${BUTTON_QA}--borderOptions`}
            borderDropdownQa={`${BUTTON_QA}--borderDropdown`}
            componentLabel={BUTTON_LABEL_TEXT}
        />
        <ColorPickerEditor
            colorList={colorList}
            firstColor={defaultColor}
            firstColorLabel={COLOR_PICKER_DEFAULT}
            changeFirstColor={(id) => props.setFormButtonBorderColorDefault(formId, id)}
            qaId={BUTTON_QA}
        />
        <ColorPickerEditor
            colorList={colorList}
            firstColor={hoverColor}
            firstColorLabel={COLOR_PICKER_HOVER}
            changeFirstColor={(id) => props.setFormButtonBorderColorHover(formId, id)}
            secondColor={focusColor}
            secondColorLabel={COLOR_PICKER_FOCUS}
            changeSecondColor={(id) => props.setFormButtonBorderColorFocus(formId, id)}
            qaId={`${BUTTON_QA}--hoverFocus`}
        />
    </SuiContainer>
);

ButtonBorderPanel.propTypes = {
    defaultColor: styleShapePropTypes,
    hoverColor: styleShapePropTypes,
    borderRadius: PropTypes.number,
    focusColor: styleShapePropTypes,
    borderWidths: PropTypes.shape({
        top: PropTypes.number,
        right: PropTypes.number,
        bottom: PropTypes.number,
        left: PropTypes.number,
    }),
    formId: PropTypes.string.isRequired,
    colorList: PropTypes.arrayOf(PropTypes.object).isRequired,
    setAllFormButtonBorderSizes: PropTypes.func.isRequired,
    setFormButtonBorderRadius: PropTypes.func.isRequired,
    setFormButtonBorderSizeTop: PropTypes.func.isRequired,
    setFormButtonBorderSizeBottom: PropTypes.func.isRequired,
    setFormButtonBorderSizeRight: PropTypes.func.isRequired,
    setFormButtonBorderSizeLeft: PropTypes.func.isRequired,
    setFormButtonBorderColorDefault: PropTypes.func.isRequired,
    setFormButtonBorderColorHover: PropTypes.func.isRequired,
    setFormButtonBorderColorFocus: PropTypes.func.isRequired,
};

ButtonBorderPanel.defaultProps = {
    defaultColor: getDefaultStyle(DEFAULT_BUTTON_BORDER_COLOR),
    hoverColor: getDefaultStyle(DEFAULT_BUTTON_BORDER_COLOR_HOVER),
    focusColor: getDefaultStyle(DEFAULT_BUTTON_BORDER_COLOR_FOCUS),
    borderRadius: DEFAULT_BUTTON_BORDER_RADIUS,
    borderWidths: {
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
    },
};

const mapStateToProps = (state) => {
    const formId = getFormId(state);
    const colorList = getColors(state);
    const { colors, radius, ...borderWidths } = getFormButtonBorderStyles(state, formId);

    const { default: defaultColor, hover: hoverColor, focus: focusColor } = colors;

    return {
        formId,
        defaultColor,
        hoverColor,
        focusColor,
        borderRadius: radius,
        borderWidths,
        colorList,
    };
};

const mapDispatchToProps = {
    setAllFormButtonBorderSizes,
    setFormButtonBorderRadius,
    setFormButtonBorderSizeTop,
    setFormButtonBorderSizeBottom,
    setFormButtonBorderSizeRight,
    setFormButtonBorderSizeLeft,
    setFormButtonBorderColorDefault,
    setFormButtonBorderColorHover,
    setFormButtonBorderColorFocus,
};

export default connect(mapStateToProps, mapDispatchToProps)(ButtonBorderPanel);
